import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtTRY, fmtDate, todayISO, CASH_ACCOUNT_LABEL } from "@/lib/format";
import { PageHeader, SectionCard, EmptyState, StatCard, StickyPageHead } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowRightLeft, Trash2, Building2, Wallet, ListOrdered } from "lucide-react";
import { toast } from "sonner";
import CashMovementsDialog from "@/components/CashMovementsDialog";

const EMPTY = { date: todayISO(), from_account: "company", to_account: "personal", amount: 0, notes: "" };

export default function CashAccounts() {
  const [balances, setBalances] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [movementsFor, setMovementsFor] = useState(null); // {account, label} | null

  const load = async () => {
    const [b, t] = await Promise.all([api.get("/cash-accounts"), api.get("/cash-transfers")]);
    setBalances(b.data); setTransfers(t.data);
  };
  useEffect(() => { load(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.from_account === form.to_account) { toast.error("Aynı kasaya transfer yapılamaz"); return; }
    try {
      await api.post("/cash-transfers", { ...form, amount: parseFloat(form.amount) || 0 });
      toast.success("Transfer kaydedildi");
      setOpen(false); setForm(EMPTY); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Hata");
    }
  };
  const onDelete = async (t) => {
    if (!window.confirm("Bu transfer silinsin mi?")) return;
    await api.delete(`/cash-transfers/${t.id}`);
    toast.success("Silindi"); load();
  };

  if (!balances) return <div className="text-sm text-slate-500">Yükleniyor…</div>;

  return (
    <div className="space-y-6" data-testid="cash-page">
      <StickyPageHead>
      <PageHeader title="Kasalar" subtitle="Şirket ve Şahsi kasalarınız arasında transfer yapın"
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800 rounded-lg" data-testid="add-transfer-button">
                <ArrowRightLeft className="w-4 h-4 mr-1.5" /> Transfer Yap
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle className="font-display">Kasalar Arası Transfer</DialogTitle></DialogHeader>
              <form onSubmit={onSubmit} className="space-y-4" data-testid="transfer-form">
                <div className="space-y-1.5">
                  <Label className="text-xs">Tarih</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-10 rounded-lg" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Kaynak</Label>
                    <Select value={form.from_account} onValueChange={(v) => setForm({ ...form, from_account: v })}>
                      <SelectTrigger className="h-10 rounded-lg" data-testid="transfer-from-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="company">Şirket Kasası</SelectItem>
                        <SelectItem value="personal">Şahsi Kasa</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Hedef</Label>
                    <Select value={form.to_account} onValueChange={(v) => setForm({ ...form, to_account: v })}>
                      <SelectTrigger className="h-10 rounded-lg" data-testid="transfer-to-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="company">Şirket Kasası</SelectItem>
                        <SelectItem value="personal">Şahsi Kasa</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Tutar (₺)</Label>
                  <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required className="h-10 rounded-lg" data-testid="transfer-amount-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Notlar</Label>
                  <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
                  <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="transfer-save-button">Kaydet</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      </StickyPageHead>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <AccountCard
          title="Şirket Kasası"
          icon={Building2}
          balances={balances.company}
          accent="blue"
          testId="company-cash-card"
          onShowMovements={() => setMovementsFor({ account: "company", label: "Şirket Kasası" })}
        />
        <AccountCard
          title="Şahsi Kasa"
          icon={Wallet}
          balances={balances.personal}
          accent="violet"
          testId="personal-cash-card"
          onShowMovements={() => setMovementsFor({ account: "personal", label: "Şahsi Kasa" })}
        />
      </div>

      <SectionCard title="Transfer Geçmişi" testId="transfers-table">
        {(transfers ?? []).length === 0 ? <EmptyState title="Henüz transfer yapılmadı" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3 font-semibold">Tarih</th>
                  <th className="px-5 py-3 font-semibold">Kaynak</th>
                  <th className="px-5 py-3 font-semibold">Hedef</th>
                  <th className="px-5 py-3 font-semibold text-right">Tutar</th>
                  <th className="px-5 py-3 font-semibold">Notlar</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(transfers ?? []).map((t) => (
                  <tr key={t.id} data-testid={`transfer-row-${t.id}`}>
                    <td className="px-5 py-3">{fmtDate(t.date)}</td>
                    <td className="px-5 py-3">{CASH_ACCOUNT_LABEL[t.from_account]}</td>
                    <td className="px-5 py-3">{CASH_ACCOUNT_LABEL[t.to_account]}</td>
                    <td className="px-5 py-3 text-right tabular-nums font-semibold">{fmtTRY(t.amount)}</td>
                    <td className="px-5 py-3 text-xs text-slate-600">{t.notes || "—"}</td>
                    <td className="px-5 py-3 text-right">
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => onDelete(t)} data-testid={`transfer-delete-${t.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <CashMovementsDialog
        open={!!movementsFor}
        onOpenChange={(v) => { if (!v) setMovementsFor(null); }}
        account={movementsFor?.account}
        accountLabel={movementsFor?.label}
      />
    </div>
  );
}

function AccountCard({ title, icon: Icon, balances, accent, testId, onShowMovements }) {
  const accentBg = accent === "blue" ? "bg-blue-50 text-blue-700" : "bg-violet-50 text-violet-700";
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{title}</div>
          <div className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-2 tabular-nums">{fmtTRY(balances.balance)}</div>
        </div>
        <div className={`w-12 h-12 rounded-xl ${accentBg} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 mt-6 pt-5 border-t border-slate-100 text-sm">
        <div>
          <div className="text-xs text-slate-500">Toplam Tahsilat</div>
          <div className="font-semibold text-emerald-600 tabular-nums mt-0.5">{fmtTRY(balances.income)}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Toplam Ödeme</div>
          <div className="font-semibold text-red-600 tabular-nums mt-0.5">{fmtTRY(balances.expense)}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Gelen Transfer</div>
          <div className="font-medium text-slate-800 tabular-nums mt-0.5">{fmtTRY(balances.transfer_in)}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Giden Transfer</div>
          <div className="font-medium text-slate-800 tabular-nums mt-0.5">{fmtTRY(balances.transfer_out)}</div>
        </div>
      </div>
      <div className="mt-5 pt-4 border-t border-slate-100">
        <Button
          variant="outline"
          size="sm"
          className="w-full h-9 rounded-lg"
          onClick={onShowMovements}
          data-testid={`${testId}-movements-button`}
        >
          <ListOrdered className="w-4 h-4 mr-1.5" /> Hareketleri Gör
        </Button>
      </div>
    </div>
  );
}
